import { FilterTableSettings, SortDirection } from '@gea/digital-ui-lib';

/**
 * Compares two values for sorting purposes.
 * @param first The first value to compare.
 * @param second The second value to compare.
 * @returns A negative number if first < second, a positive number if first > second, or 0 if they are equal.
 */
function compareValues(first: unknown, second: unknown): number {
  if (first == null && second == null) return 0;
  if (first == null) return -1;
  if (second == null) return 1;

  if (typeof first === 'string' && typeof second === 'string') {
    return first.localeCompare(second);
  }

  const numA = Number(first);
  const numB = Number(second);
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }

  return String(first).localeCompare(String(second));
}

/**
 * Sorts the given data array based on the table filter settings.
 * @param tableFilter The table filter settings to apply.
 * @param data The data array to sort.
 * @returns The sorted data array.
 */
export function sortTableData<T>(tableFilter: FilterTableSettings, data: T[]): T[] {
  const sortColumns = Object.entries(tableFilter.columns)
    .map(([key, col]) => ({ key: key as keyof T, sortDirection: col?.sort }))
    .filter(({ sortDirection }) => !!sortDirection);

  if (sortColumns.length === 0) return data;

  return [...data].sort((a, b) => {
    for (const { key, sortDirection } of sortColumns) {
      const asc = sortDirection === SortDirection.ASCENDING;
      const first = a[key];
      const second = b[key];

      const cmp = compareValues(first, second);

      if (cmp !== 0) return asc ? cmp : -cmp;
    }
    return 0;
  });
}

/**
 * Handles the table filters and sorting for the given data array.
 * @param tableFilter The table filter settings to apply.
 * @param data The data array to filter and sort.
 * @returns The filtered and sorted data array.
 */
export function handleTableFilters<T>(tableFilter: FilterTableSettings, data: T[]): T[] {
  const filteredData = Object.entries(tableFilter.columns).reduce((acc, [name, { filter }]) => {
    if (filter?.length) {
      return filterByColumnName(name as keyof T, filter, acc);
    }
    return acc;
  }, data);

  return sortTableData(tableFilter, filteredData);
}

/**
 * Inverts the sort direction for each column in the table settings.
 * @param settings The table settings to normalize.
 * @returns The normalized table settings with sort directions inverted.
 */
export function invertSortDirectionsForClientSorting(settings: FilterTableSettings): FilterTableSettings {
  const columns = { ...settings.columns };

  for (const [key, column] of Object.entries(columns)) {
    if (column.sort === SortDirection.ASCENDING) {
      columns[key] = { ...column, sort: SortDirection.DESCENDING };
    } else if (column.sort === SortDirection.DESCENDING) {
      columns[key] = { ...column, sort: SortDirection.ASCENDING };
    }
  }

  return { ...settings, columns };
}

/**
 * Filters the tree nodes based on the table filter settings, including both column filters and sort order.
 * @param tableFilter The table filter settings to apply.
 * @param nodes The tree nodes to filter.
 * @returns The filtered tree nodes.
 */
export function handleTableTreeFilters<TData, TNode extends { data?: TData; children?: TNode[] }>(
  tableFilter: FilterTableSettings,
  nodes: TNode[]
): TNode[] {
  const hasFilters = Object.values(tableFilter.columns ?? {}).some((column) => column?.filter?.length);
  if (!hasFilters) {
    return nodes;
  }

  /**
   * Recursively filters a tree node and its children based on the table filter settings.
   * @param node The tree node to filter.
   * @returns The filtered tree node, or undefined if it doesn't match the filter criteria.
   */
  const filterNode = (node: TNode): TNode | undefined => {
    const children = node.children?.map(filterNode).filter((child): child is TNode => !!child);
    const matches = node.data != null && handleTableFilters<TData>(tableFilter, [node.data]).length > 0;

    if (!matches && !children?.length) {
      return undefined;
    }

    return { ...node, children } as TNode;
  };

  return nodes.map(filterNode).filter((node): node is TNode => !!node);
}

/**
 * Filters the data array based on the specified column and filter values.
 * @param column The column to filter by.
 * @param filter The filter values to apply.
 * @param data The data array to filter.
 * @returns The filtered data array.
 */
export function filterByColumnName<T>(column: keyof T, filter: string[] | boolean[], data: T[]): T[] {
  const normalizedFilters = filter.map((f) => f.toString().toLowerCase());
  return data.filter((temp) => {
    const value = (temp[column] ?? '').toString().toLowerCase();
    return normalizedFilters.some((currentFilter) => value.includes(currentFilter));
  });
}

/**
 * Sorts the data array based on the specified column and sort direction.
 * @param column The column to sort by.
 * @param sortDirection The sort direction (ascending or descending).
 * @param data The data array to sort.
 * @returns The sorted data array.
 */
export function sortByColumnName<T>(column: keyof T, sortDirection: SortDirection, data: T[]): T[] {
  const result = [...data].sort((a, b) => {
    const first = a[column];
    const second = b[column];
    const cmp = compareValues(first, second);

    return sortDirection === SortDirection.ASCENDING ? cmp : -cmp;
  });

  return result;
}

/**
 * Loads the table settings from the session storage.
 * @param tableSettingsKey The key used to store the table settings in the session storage.
 * @returns The loaded table settings, or default settings if none are found or if parsing fails.
 */
export function loadTableSettingsFromSession(tableSettingsKey: string): FilterTableSettings {
  const stored = sessionStorage.getItem(tableSettingsKey);

  if (stored) {
    try {
      const settings = JSON.parse(stored);
      return settings;
    } catch {
      // Ignore invalid stored settings
    }
  }
  return { columns: {} } as FilterTableSettings;
}
